/**
 * 系統備份頁(REQ-20260522、REQ-20260729003):
 *  - 資料庫備份:一鍵 mysqldump → MinIO,可下載 / 刪除
 *  - 附件備份:把 MinIO 內的附件複製到**主機磁碟**,只能列出 / 刪除(取回走 SSH)
 *  - 每日自動備份排程 + 兩種備份各自的保留天數
 *  - ADMIN-only(由 settings router + backend RolesGuard 兩層保護)
 */
import axios from 'axios';
import { useEffect, useState } from 'react';
import type {
  ApiSuccess,
  BackupItem,
  BackupDownloadUrl,
  BackupScheduleView,
  FileBackupCreateResult,
  FileBackupItem,
} from '@app/shared';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { apiClient } from '../../lib/api-client';

/** 待確認的動作。用一個 state 帶著,整頁共用同一個對話框實例 */
interface PendingConfirm {
  title: string;
  message: React.ReactNode;
  confirmLabel: string;
  tone: 'primary' | 'danger';
  run: () => void;
}

function fmtSize(b: number): string {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  if (b < 1024 * 1024 * 1024) return `${(b / 1024 / 1024).toFixed(1)} MB`;
  return `${(b / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

function extractApiError(e: unknown, fallback: string): string {
  if (axios.isAxiosError(e)) {
    const data = e.response?.data as { error?: { message?: string } } | undefined;
    return data?.error?.message ?? e.message ?? fallback;
  }
  return e instanceof Error ? e.message : fallback;
}

function fmtDateTime(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString('zh-TW');
}

export function SystemBackupPage(): JSX.Element {
  const [items, setItems] = useState<BackupItem[]>([]);
  const [fileItems, setFileItems] = useState<FileBackupItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [creatingFiles, setCreatingFiles] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'sql' | 'files'>('sql');
  const [pending, setPending] = useState<PendingConfirm | null>(null);

  const load = async (): Promise<void> => {
    setLoading(true);
    try {
      const [sql, files] = await Promise.all([
        apiClient.get<ApiSuccess<BackupItem[]>>('/admin/backups'),
        apiClient.get<ApiSuccess<FileBackupItem[]>>('/admin/backups/files'),
      ]);
      setItems(sql.data.data);
      setFileItems(files.data.data);
    } catch (e) {
      setErr(extractApiError(e, '讀取備份列表失敗'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const askCreateFiles = (): void =>
    setPending({
      title: '立即備份附件檔案?',
      message: (
        <>
          會把所有訪談附件複製到<strong>主機磁碟</strong>。
          <br />
          資料量大時可能需要<strong>數分鐘</strong>,期間可以繼續使用系統。
        </>
      ),
      confirmLabel: '開始備份',
      tone: 'primary',
      run: () => void onCreateFiles(),
    });

  const onCreateFiles = async (): Promise<void> => {
    setCreatingFiles(true);
    setErr(null);
    setInfo(null);
    try {
      // 附件量大(數 GB / 上萬個檔),給 30 分鐘
      const res = await apiClient.post<ApiSuccess<FileBackupCreateResult>>(
        '/admin/backups/files',
        null,
        { timeout: 1_800_000 },
      );
      const r = res.data.data;
      setInfo(
        `附件備份完成:${r.name}(${r.fileCount} 個檔案 / ${fmtSize(r.sizeBytes)})` +
          (r.skippedCount > 0
            ? ` ⚠ 有 ${r.skippedCount} 個檔案讀取失敗未納入,這份快照不完整`
            : ''),
      );
      void load();
    } catch (e) {
      setErr(extractApiError(e, '附件備份失敗'));
    } finally {
      setCreatingFiles(false);
    }
  };

  const askDeleteFiles = (name: string): void =>
    setPending({
      title: '刪除附件備份',
      message: (
        <>
          確定要刪除快照 <span className="font-mono">{name}</span>?
          <br />
          此動作<strong>無法復原</strong>。
        </>
      ),
      confirmLabel: '刪除',
      tone: 'danger',
      run: () => void onDeleteFiles(name),
    });

  const onDeleteFiles = async (name: string): Promise<void> => {
    setErr(null);
    setInfo(null);
    try {
      await apiClient.delete(`/admin/backups/files/${encodeURIComponent(name)}`);
      setInfo(`已刪除附件備份:${name}`);
      void load();
    } catch (e) {
      setErr(extractApiError(e, '刪除失敗'));
    }
  };

  const askCreate = (): void =>
    setPending({
      title: '立即備份資料庫?',
      message: (
        <>
          會把整個 MySQL 資料庫匯出為 <code>.sql.gz</code> 並存進 MinIO。
          <br />
          通常需要 30-60 秒。
        </>
      ),
      confirmLabel: '開始備份',
      tone: 'primary',
      run: () => void onCreate(),
    });

  const onCreate = async (): Promise<void> => {
    setCreating(true);
    setErr(null);
    setInfo(null);
    try {
      // 備份大資料庫可能要 30-60 秒,timeout 設 5 分鐘
      const res = await apiClient.post<ApiSuccess<BackupItem>>('/admin/backups', null, {
        timeout: 300_000,
      });
      setInfo(`備份完成:${res.data.data.filename}(${fmtSize(res.data.data.sizeBytes)})`);
      void load();
    } catch (e) {
      setErr(extractApiError(e, '備份失敗'));
    } finally {
      setCreating(false);
    }
  };

  const onDownload = async (filename: string): Promise<void> => {
    try {
      const res = await apiClient.get<ApiSuccess<BackupDownloadUrl>>(
        `/admin/backups/${encodeURIComponent(filename)}/url`,
      );
      window.open(res.data.data.url, '_blank', 'noopener');
    } catch (e) {
      setErr(extractApiError(e, '取下載連結失敗'));
    }
  };

  const askDelete = (filename: string): void =>
    setPending({
      title: '刪除資料庫備份',
      message: (
        <>
          確定要刪除備份檔 <span className="font-mono break-all">{filename}</span>?
          <br />
          此動作<strong>無法復原</strong>。
        </>
      ),
      confirmLabel: '刪除',
      tone: 'danger',
      run: () => void onDelete(filename),
    });

  const onDelete = async (filename: string): Promise<void> => {
    setErr(null);
    setInfo(null);
    try {
      await apiClient.delete(`/admin/backups/${encodeURIComponent(filename)}`);
      setInfo(`已刪除:${filename}`);
      void load();
    } catch (e) {
      setErr(extractApiError(e, '刪除失敗'));
    }
  };

  return (
    <section className="grid gap-4 max-w-4xl">
      <h2 className="text-lg font-semibold">系統備份</h2>
      <p className="text-xs text-slate-600 leading-relaxed">
        備份分成兩份、存在兩個地方,刻意不放在一起 ——
        <strong>資料庫</strong>匯出為 <code>.sql.gz</code> 存進 MinIO;
        <strong>訪談附件</strong>則複製到<strong>主機磁碟</strong>。
        附件本體就在 MinIO,備份回同一個地方等於沒備份(硬碟壞掉兩份一起沒)。
        <br />
        因為存在主機上,附件備份無法從這個畫面下載,需要取回時請由維運人員從主機取得;
        資料庫備份仍可直接下載。
        「我獨自升級」每次套用變更前會自動執行一次資料庫備份,不需手動先按。
      </p>

      <BackupSchedulePanel onRan={() => void load()} onError={setErr} onInfo={setInfo} />

      {/* 分頁標籤:兩種備份的欄位與可用操作都不同,並排會看不出哪個是哪個。
          樣式沿用 SmartAdjustPage 的 TabButton,不另創一套。 */}
      <div className="flex gap-1 border-b" data-testid="backup-tabs">
        <BackupTab
          active={activeTab === 'sql'}
          onClick={() => setActiveTab('sql')}
          testId="tab-sql"
        >
          資料庫備份 ({items.length})
        </BackupTab>
        <BackupTab
          active={activeTab === 'files'}
          onClick={() => setActiveTab('files')}
          testId="tab-files"
        >
          附件備份 ({fileItems.length})
        </BackupTab>
      </div>

      <div className="bg-white border rounded p-4 grid gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          {/* 備份按鈕跟著分頁走 —— 看著附件列表卻按到資料庫備份是很容易發生的誤操作 */}
          {activeTab === 'sql' ? (
            <button
              type="button"
              onClick={askCreate}
              disabled={creating || creatingFiles}
              className="bg-blue-600 text-white px-3 py-1.5 rounded hover:bg-blue-700 disabled:opacity-50"
            >
              {creating ? '資料庫備份中…(可能需 30-60 秒)' : '立即備份資料庫'}
            </button>
          ) : (
            <button
              type="button"
              onClick={askCreateFiles}
              disabled={creating || creatingFiles}
              className="bg-indigo-600 text-white px-3 py-1.5 rounded hover:bg-indigo-700 disabled:opacity-50"
            >
              {creatingFiles ? '附件備份中…(資料量大,可能數分鐘)' : '立即備份附件'}
            </button>
          )}
          <button
            type="button"
            onClick={() => void load()}
            disabled={loading}
            className="border rounded px-3 py-1.5 text-slate-700 hover:bg-slate-100 disabled:opacity-50"
          >
            {loading ? '更新中…' : '重新整理列表'}
          </button>
          {/* 另一種備份正在跑時要講出來,否則切走分頁就看不到任何進行中的跡象 */}
          {activeTab === 'sql' && creatingFiles ? (
            <span className="text-xs text-slate-500">附件備份執行中…</span>
          ) : null}
          {activeTab === 'files' && creating ? (
            <span className="text-xs text-slate-500">資料庫備份執行中…</span>
          ) : null}
        </div>
        {info ? (
          <div className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded p-2">
            {info}
          </div>
        ) : null}
        {err ? (
          <div role="alert" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2">
            {err}
          </div>
        ) : null}
      </div>

      {activeTab === 'sql' ? (
      <div className="bg-white border rounded p-4">
        <div className="text-sm font-medium text-slate-800 mb-2">
          資料庫備份(共 {items.length} 個,存放於 MinIO)
        </div>
        {items.length === 0 ? (
          <div className="text-sm text-slate-500 p-3">尚無備份檔</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-xs text-slate-600">
                <th className="px-2 py-1.5">檔名</th>
                <th className="px-2 py-1.5 w-28 text-right">大小</th>
                <th className="px-2 py-1.5 w-44">建立時間</th>
                <th className="px-2 py-1.5 w-32 text-right">操作</th>
              </tr>
            </thead>
            <tbody>
              {items.map((b) => (
                <tr key={b.filename} className="border-b last:border-b-0 hover:bg-slate-50">
                  <td className="px-2 py-1.5 font-mono text-xs">{b.filename}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums">{fmtSize(b.sizeBytes)}</td>
                  <td className="px-2 py-1.5 text-xs">
                    {b.createdAt ? new Date(b.createdAt).toLocaleString('zh-TW') : '—'}
                  </td>
                  <td className="px-2 py-1.5 text-right">
                    <button
                      type="button"
                      onClick={() => void onDownload(b.filename)}
                      className="text-blue-700 hover:underline mr-3 text-xs"
                    >
                      下載
                    </button>
                    <button
                      type="button"
                      onClick={() => askDelete(b.filename)}
                      className="text-red-700 hover:underline text-xs"
                    >
                      刪除
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      ) : null}

      {activeTab === 'files' ? (
      <div className="bg-white border rounded p-4">
        <div className="text-sm font-medium text-slate-800 mb-2">
          附件備份(共 {fileItems.length} 份,存放於主機磁碟)
        </div>
        {fileItems.length === 0 ? (
          <div className="text-sm text-slate-500 p-3">
            尚無附件備份。按上方「立即備份附件」可手動執行一次,或在排程設定啟用每日自動備份。
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-xs text-slate-600">
                <th className="px-2 py-1.5">快照</th>
                <th className="px-2 py-1.5 w-24 text-right">檔案數</th>
                <th className="px-2 py-1.5 w-28 text-right">大小</th>
                <th className="px-2 py-1.5 w-44">建立時間</th>
                <th className="px-2 py-1.5 w-20 text-right">操作</th>
              </tr>
            </thead>
            <tbody>
              {fileItems.map((f) => (
                <tr key={f.name} className="border-b last:border-b-0 hover:bg-slate-50">
                  <td className="px-2 py-1.5 font-mono text-xs">{f.name}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums">{f.fileCount}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums">{fmtSize(f.sizeBytes)}</td>
                  <td className="px-2 py-1.5 text-xs">{fmtDateTime(f.createdAt)}</td>
                  <td className="px-2 py-1.5 text-right">
                    <button
                      type="button"
                      onClick={() => askDeleteFiles(f.name)}
                      className="text-red-700 hover:underline text-xs"
                    >
                      刪除
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      ) : null}

      <ConfirmDialog
        open={pending !== null}
        title={pending?.title ?? ''}
        message={pending?.message ?? null}
        confirmLabel={pending?.confirmLabel ?? '確定'}
        tone={pending?.tone ?? 'primary'}
        onConfirm={() => pending?.run()}
        onClose={() => setPending(null)}
      />
    </section>
  );
}

/**
 * 分頁標籤按鈕。樣式與 SmartAdjustPage / CustomerConsolePage 的 TabButton 一致 ——
 * 都是「同一區塊內切換不同資料視圖」,不另創一套視覺。
 */
function BackupTab(props: {
  active: boolean;
  onClick: () => void;
  testId: string;
  children: React.ReactNode;
}): JSX.Element {
  const base = 'px-4 py-2 text-sm border-b-2 -mb-px transition-colors';
  const cls = props.active
    ? 'border-blue-600 text-blue-700 font-semibold'
    : 'border-transparent text-slate-600 hover:text-slate-900';
  return (
    <button
      type="button"
      onClick={props.onClick}
      className={`${base} ${cls}`}
      data-testid={props.testId}
    >
      {props.children}
    </button>
  );
}

/**
 * 每日自動備份設定。
 *
 * 兩種備份的保留天數分開設定:附件一份快照約等於 MinIO 現有用量(數 GB),
 * 留跟資料庫一樣久會吃掉主機磁碟;資料庫 dump 只有幾 MB,留久一點成本很低。
 */
function BackupSchedulePanel({
  onRan,
  onError,
  onInfo,
}: {
  onRan: () => void;
  onError: (msg: string | null) => void;
  onInfo: (msg: string | null) => void;
}): JSX.Element {
  const [view, setView] = useState<BackupScheduleView | null>(null);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [confirmRun, setConfirmRun] = useState(false);

  const load = async (): Promise<void> => {
    try {
      const res = await apiClient.get<ApiSuccess<BackupScheduleView>>('/admin/backups/schedule');
      setView(res.data.data);
    } catch (e) {
      onError(extractApiError(e, '讀取備份排程失敗'));
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const save = async (next: BackupScheduleView): Promise<void> => {
    setSaving(true);
    onError(null);
    onInfo(null);
    try {
      const res = await apiClient.put<ApiSuccess<BackupScheduleView>>(
        '/admin/backups/schedule',
        {
          enabled: next.enabled,
          hour: next.hour,
          minute: next.minute,
          sqlRetentionDays: next.sqlRetentionDays,
          fileRetentionDays: next.fileRetentionDays,
        },
      );
      setView(res.data.data);
      onInfo('備份排程已儲存');
    } catch (e) {
      onError(extractApiError(e, '儲存備份排程失敗'));
    } finally {
      setSaving(false);
    }
  };

  const runNow = async (): Promise<void> => {
    setRunning(true);
    onError(null);
    onInfo(null);
    try {
      const res = await apiClient.post<ApiSuccess<{ summary: string }>>(
        '/admin/backups/run-now',
        null,
        { timeout: 1_800_000 },
      );
      onInfo(`執行完成:${res.data.data.summary}`);
      void load();
      onRan();
    } catch (e) {
      onError(extractApiError(e, '執行失敗'));
    } finally {
      setRunning(false);
    }
  };

  if (!view) {
    return (
      <div role="status" className="bg-white border rounded p-4 text-sm text-slate-500">
        讀取排程設定中…
      </div>
    );
  }

  const busy = saving || running;

  return (
    <div className="bg-white border rounded p-4 grid gap-3">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-sm font-medium text-slate-800">每日自動備份</span>
        <label className="inline-flex items-center gap-1.5 text-sm">
          <input
            type="checkbox"
            checked={view.enabled}
            disabled={busy}
            onChange={(e) => void save({ ...view, enabled: e.target.checked })}
          />
          啟用
        </label>
        <span className="text-xs text-slate-500">
          {view.enabled
            ? `下次執行:${fmtDateTime(view.nextRunAt)}`
            : '目前只會在手動按下時執行'}
        </span>
        <button
          type="button"
          onClick={() => setConfirmRun(true)}
          disabled={busy}
          className="ml-auto border rounded px-3 py-1 text-xs text-slate-700 hover:bg-slate-100 disabled:opacity-50"
        >
          {running ? '執行中…' : '立刻執行一次'}
        </button>
      </div>

      <ConfirmDialog
        open={confirmRun}
        title="立刻執行一次完整流程?"
        message={
          <>
            會依序執行:<strong>資料庫備份</strong> → <strong>附件備份</strong> →
            依保留天數<strong>清掉過期備份</strong>。
            <br />
            附件資料量大時可能需要數分鐘。
          </>
        }
        confirmLabel="開始執行"
        onConfirm={() => void runNow()}
        onClose={() => setConfirmRun(false)}
      />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
        <label className="grid gap-1">
          <span className="text-xs text-slate-600">執行時間(台灣時間)</span>
          <div className="flex items-center gap-1">
            <NumberInput
              value={view.hour}
              min={0}
              max={23}
              disabled={busy}
              onCommit={(hour) => void save({ ...view, hour })}
            />
            <span className="text-slate-500">:</span>
            <NumberInput
              value={view.minute}
              min={0}
              max={59}
              disabled={busy}
              onCommit={(minute) => void save({ ...view, minute })}
            />
          </div>
        </label>
        <label className="grid gap-1">
          <span className="text-xs text-slate-600">資料庫備份保留(天)</span>
          <NumberInput
            value={view.sqlRetentionDays}
            min={1}
            max={365}
            disabled={busy}
            onCommit={(sqlRetentionDays) => void save({ ...view, sqlRetentionDays })}
          />
        </label>
        <label className="grid gap-1">
          <span className="text-xs text-slate-600">附件備份保留(天)</span>
          <NumberInput
            value={view.fileRetentionDays}
            min={1}
            max={90}
            disabled={busy}
            onCommit={(fileRetentionDays) => void save({ ...view, fileRetentionDays })}
          />
          <span className="text-[11px] text-slate-400">
            一份附件快照約等於目前附件總量,留太多天會吃掉主機磁碟。
          </span>
        </label>
      </div>

      {view.lastRunAt ? (
        <div className="text-xs text-slate-600 border-t pt-2">
          上次執行:{fmtDateTime(view.lastRunAt)}
          <div className="text-slate-500 mt-0.5">{view.lastRunSummary}</div>
          <div className="text-[11px] text-slate-400 mt-0.5">
            (此紀錄只保留在記憶體,系統重啟後會清空)
          </div>
        </div>
      ) : null}
    </div>
  );
}

/**
 * 數字輸入框:失焦或按 Enter 才送出,避免每打一個字就存一次設定。
 * 超出範圍會夾回邊界,不讓不合法的值送到後端被 400 打回來。
 */
function NumberInput({
  value,
  min,
  max,
  disabled,
  onCommit,
}: {
  value: number;
  min: number;
  max: number;
  disabled: boolean;
  onCommit: (v: number) => void;
}): JSX.Element {
  const [draft, setDraft] = useState(String(value));

  useEffect(() => {
    setDraft(String(value));
  }, [value]);

  const commit = (): void => {
    const n = Number.parseInt(draft, 10);
    const safe = Number.isNaN(n) ? value : Math.min(max, Math.max(min, n));
    setDraft(String(safe));
    if (safe !== value) onCommit(safe);
  };

  return (
    <input
      type="number"
      value={draft}
      min={min}
      max={max}
      disabled={disabled}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') e.currentTarget.blur();
      }}
      className="border rounded px-2 py-1 text-sm w-20 focus:outline-none focus:ring-2 focus:ring-blue-400 disabled:opacity-50"
    />
  );
}
